import { ChevronLeft, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "../atoms/Button";
import { Heading } from "../atoms/Heading";
import { Text } from "../atoms/Text";
import { cn } from "../lib/cn";
import { BreadcrumbTrail } from "../molecules/BreadcrumbTrail";
import { ErrorState } from "../molecules/ErrorState";
import { Skeleton } from "../molecules/Skeleton";
import {
	TRUST_STATUS_COPY,
	TrustBadge,
	type TrustStatus,
} from "../molecules/TrustBadge";
import { BatchSummaryCard } from "../organisms/BatchSummaryCard";
import type { BatchRow } from "../organisms/BatchTable";
import { EventTimeline } from "../organisms/EventTimeline";
import type { EventTimelineItem } from "../organisms/EventTimeline";

export interface BatchDetailBatch extends BatchRow {
	previousBatchRoot?: string | null;
	nextBatchRoot?: string | null;
}

export interface BatchDetailPageProps {
	batch: BatchDetailBatch | null;
	loading?: boolean;
	error?: Error | null;
	onPrev: () => void;
	onNext: () => void;
	events?: EventTimelineItem[];
	eventsLoading?: boolean;
	className?: string;
}

/**
 * A batch is anchored once its merkle root is committed on-chain. Until then it
 * is sealed but still being confirmed, or — with no events yet — still being
 * prepared. Batches have no per-record "verified" flag, so authenticity is
 * inferred from anchoring and contents.
 */
function deriveBatchTrust(batch: BatchDetailBatch): TrustStatus {
	if (batch.anchored) {
		return "verified";
	}
	if (batch.recordCount > 0) {
		return "recorded";
	}
	return "processing";
}

function batchTitle(batch: BatchDetailBatch, status: TrustStatus): string {
	const noun = status === "processing" ? "Record" : "Sealed record";
	return `${noun} · ${batch.createdAt}`;
}

function BatchDetailPage({
	batch,
	loading = false,
	error = null,
	onPrev,
	onNext,
	events = [],
	eventsLoading = false,
	className,
}: BatchDetailPageProps): ReactNode {
	const trustStatus = batch ? deriveBatchTrust(batch) : null;
	const title =
		batch && trustStatus ? batchTitle(batch, trustStatus) : "Detail";
	const breadcrumbs = [
		{ label: "Batches", href: "/batches" },
		{ label: title },
	];

	return (
		<div className={cn("flex flex-col gap-6", className)}>
			<BreadcrumbTrail items={breadcrumbs} />

			{error ? (
				<ErrorState error={error} title="Failed to load batch" />
			) : loading ? (
				<div className="flex flex-col gap-4">
					<Skeleton className="h-8 w-48" variant="text" />
					<Skeleton className="h-48 w-full" />
				</div>
			) : batch && trustStatus ? (
				<>
					{/* Authenticity verdict, leading the page */}
					<div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-5">
						<div className="flex flex-wrap items-center gap-3">
							<TrustBadge status={trustStatus} />
							<Heading size="xl">{title}</Heading>
						</div>
						<Text textColor="mutedForeground">
							{TRUST_STATUS_COPY[trustStatus].verdict}
						</Text>
						<Text size="sm" textColor="mutedForeground">
							{batch.recordCount > 0
								? `${batch.recordCount} events from across loans, sealed together.`
								: "No events sealed in this record yet."}
						</Text>
					</div>

					<BatchSummaryCard {...batch} />

					{/* Events in this batch */}
					<div className="flex flex-col gap-3">
						<Heading size="md">Events in this record</Heading>
						<EventTimeline
							events={events}
							loading={eventsLoading}
							emptyMessage="No events recorded in this batch."
						/>
					</div>

					{/* Navigation */}
					<div className="flex items-center justify-between border-t border-border pt-4">
						<Button
							variant="outline"
							onClick={onPrev}
							disabled={!batch.previousBatchRoot}
						>
							<ChevronLeft className="mr-1 h-4 w-4" aria-hidden="true" />
							Previous
						</Button>
						{batch.previousBatchRoot || batch.nextBatchRoot ? (
							<Text size="xs" textColor="mutedForeground">
								Browse sealed records
							</Text>
						) : null}
						<Button
							variant="outline"
							onClick={onNext}
							disabled={!batch.nextBatchRoot}
						>
							Next
							<ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
						</Button>
					</div>
				</>
			) : (
				<Text textColor="mutedForeground">Batch not found.</Text>
			)}
		</div>
	);
}
BatchDetailPage.displayName = "BatchDetailPage";

export default BatchDetailPage;
export { BatchDetailPage };
